import React from 'react';
import { motion } from 'framer-motion';

const HomeAbout = ({ content }) => {
    const getSection = (name) => content?.find(c => c.section_name === name);

    const intro = getSection('home_intro');
    const vision = getSection('vision');
    const mission = getSection('mission');

    if (!intro && !vision && !mission) return null;

    return (
        <section className="py-20 md:py-32 bg-white relative overflow-hidden" id="about">
            {/* Background Decorative Element */}
            <div className="absolute top-40 -left-20 w-80 h-80 bg-agro-lime/5 rounded-full blur-3xl"></div>

            <div className="container mx-auto px-6 relative z-10">

                {/* Intro Section */}
                {intro && (
                    <div className="flex flex-col lg:flex-row gap-16 mb-32 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="lg:w-2/5"
                        >
                            <p className="text-agro-green font-sans uppercase tracking-[0.3em] text-xs mb-4 font-black">
                                Since inception
                            </p>
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight whitespace-pre-line">
                                {intro.title.replace('About ', 'About\n').replace('Agro Farms', 'Agro\u00A0Farms')}
                            </h2>
                            <div className="w-20 h-1 bg-agro-lime mt-8 rounded-full"></div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="lg:w-3/5"
                        >
                            <div className="prose prose-xl text-gray-600 font-sans italic">
                                {intro.content.split('\n').map((line, i) => (
                                    <p key={i} className="mb-6 text-xl md:text-2xl leading-relaxed font-light text-gray-500">
                                        "{line}"
                                    </p>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Vision & Mission Grid with floating effect */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {/* Vision */}
                    {vision && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.8 }}
                            className="bg-[#0a221a] text-white p-12 md:p-16 rounded-[3rem] shadow-2xl relative overflow-hidden group"
                        >
                            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full transition-transform duration-700 group-hover:scale-150"></div>
                            <div className="relative z-10">
                                <div className="w-12 h-12 bg-agro-lime/20 rounded-xl mb-8 flex items-center justify-center">
                                    <div className="w-6 h-6 bg-agro-lime rounded-full"></div>
                                </div>
                                <h3 className="text-4xl font-serif mb-8 text-agro-lime">{vision.title}</h3>
                                <p className="text-agro-cream/70 text-xl leading-relaxed font-light">
                                    {vision.content}
                                </p>
                            </div>
                        </motion.div>
                    )}

                    {/* Mission */}
                    {mission && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="bg-white border border-gray-100 p-12 md:p-16 rounded-[3rem] shadow-2xl relative overflow-hidden group"
                        >
                            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-agro-lime/5 rounded-full transition-transform duration-700 group-hover:scale-150"></div>
                            <h3 className="text-4xl font-serif mb-8 text-agro-green-dark">{mission.title}</h3>
                            <div className="text-gray-500 text-xl leading-relaxed space-y-4 font-light">
                                {mission.content.split('\n').map((line, i) => (
                                    <p key={i} className="flex items-start group/item">
                                        {line.trim().startsWith('•') ? (
                                            <>
                                                <span className="text-agro-lime mr-4 mt-2.5 w-2 h-2 rounded-full bg-agro-lime transform group-hover/item:scale-150 transition-transform"></span>
                                                <span className="flex-1 group-hover/item:text-agro-green-dark transition-colors">{line.replace('•', '').trim()}</span>
                                            </>
                                        ) : line}
                                    </p>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </div>

            </div>
        </section>
    );
};

export default HomeAbout;
